import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
});

// Evento emitido cuando una request autenticada recibe 401 (sesión inválida/expirada).
// AuthContext escucha este evento para mostrar un aviso controlado en vez de
// forzar una recarga dura de la SPA (que perdería cualquier formulario en curso).
export const SESSION_EXPIRED_EVENT = 'riq:session-expired';

// Evita que varias requests que fallan casi al mismo tiempo (p. ej. las llamadas
// en paralelo de un Promise.all) disparen el evento — y por lo tanto el aviso — más de una vez.
let sessionExpiredNotified = false;
export function resetSessionExpiredGuard() {
  sessionExpiredNotified = false;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('riq_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Extraída como función nombrada (en vez de un arrow anónimo inline) solo para
// poder testearla de forma aislada sin hacer requests reales — misma lógica,
// mismo comportamiento, ahora con nombre propio.
export function manejarErrorDeRespuesta(error) {
  if (error.response?.status === 401) {
    const habiaToken = !!localStorage.getItem('riq_token');
    localStorage.removeItem('riq_token');
    // `skipSessionHandling` lo pasan explícitamente las llamadas que NO representan
    // una sesión ya autenticada (login, registro, google, verificar sesión al abrir
    // la app): ahí un 401 es "credenciales inválidas" o "token viejo silencioso",
    // nunca "se te venció la sesión mientras trabajabas". Es un opt-out explícito
    // por request, no una heurística sobre la URL.
    const esSesionActiva = habiaToken && !error.config?.skipSessionHandling;
    if (esSesionActiva && !sessionExpiredNotified) {
      sessionExpiredNotified = true;
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  }
  return Promise.reject(error);
}

api.interceptors.response.use((response) => response, manejarErrorDeRespuesta);

// ─── Ingredientes ─────────────────────────────────────────
// Fase 2A.4-R — original de la factura y reintento de lectura. Ambos usan el
// cliente autenticado de siempre; nunca construyen rutas de Storage.
export const documentosAPI = {
  obtenerUrl: (documentoId) => api.get(`/api/documentos/${documentoId}/url`),
  reintentar: (documentoId) => api.post(`/api/documentos/${documentoId}/reintentar`),
};

// Fase 2B.1/2B.2 — Financial Inbox, solo lectura. `listar` acepta
// {status, source (Fase 6.3D.6), search, limit, cursor}; `cursor` es el `next_cursor` opaco que
// ya devolvió el backend — nunca se construye ni se decodifica aquí.
export const facturasAPI = {
  listar: (params) => api.get('/api/facturas', { params }),
  obtener: (id) => api.get(`/api/facturas/${id}`),
};

// Fase 5.1 — "Recepción automática": la dirección de correo de facturas es
// SIEMPRE la del restaurante de la sesión (el servidor lo decide; nunca se
// envía un restaurante). Ningún correo aplica nada: solo llega a revisión.
export const correoFacturasAPI = {
  obtener: () => api.get('/api/facturas/correo'),
  crear: () => api.post('/api/facturas/correo'),
  regenerar: () => api.post('/api/facturas/correo/regenerar'),
  desactivar: () => api.post('/api/facturas/correo/desactivar'),
  reactivar: () => api.post('/api/facturas/correo/reactivar'),
  mensajes: () => api.get('/api/facturas/correo/mensajes'),
};

// Fase 6.2A — Configuración > Integraciones > WhatsApp (el servidor decide
// la disponibilidad; nunca se envía restaurante ni usuario desde aquí).
export const whatsappAPI = {
  estado: () => api.get('/api/integraciones/whatsapp'),
  generarCodigo: () => api.post('/api/integraciones/whatsapp/codigo'),
  confirmar: (id) => api.post(`/api/integraciones/whatsapp/vinculos/${id}/confirmar`),
  revocar: (id) => api.post(`/api/integraciones/whatsapp/vinculos/${id}/revocar`),
  // Fase 6.3D.6 — facturas recibidas por WhatsApp (solo lectura; admin y gerente).
  facturas: () => api.get('/api/integraciones/whatsapp/facturas'),
};

export const ingredientesAPI = {
  listar: (params) => api.get('/api/ingredientes', { params }),
  obtener: (id) => api.get(`/api/ingredientes/${id}`),
  crear: (data) => api.post('/api/ingredientes', data),
  actualizar: (id, data) => api.put(`/api/ingredientes/${id}`, data),
  eliminar: (id) => api.delete(`/api/ingredientes/${id}`),
  // Multiselección + DESACTIVACIÓN masiva (nunca un delete físico — ver
  // backend/routes/ingredientes.js). POST con body JSON estable, per la
  // corrección de auditoría confirmada: más predecible en este stack que
  // depender de que cada capa intermedia preserve un body en DELETE.
  eliminarBulk: (ids) => api.post('/api/ingredientes/bulk-delete', { ids }),
  // Reactivación (Master Price — Fase 2): inversa de eliminarBulk, mismo
  // shape de payload ({ids}) — reutilizada tal cual para reactivación
  // individual (ids: [unSoloId]) desde Ingredientes.js y ModalFactura.js.
  // Fase 5.7A — `extra` (opcional) lleva { factura_id } desde la revisión de facturas.
  reactivarBulk: (ids, extra = {}) => api.post('/api/ingredientes/bulk-reactivate', { ids, ...extra }),
  // Fusionar ingredientes (Fase 4) — preview es SOLO LECTURA (puede
  // llamarse tantas veces como el admin cambie de canónico dentro del
  // modal); merge ejecuta la fusión real vía la RPC transaccional.
  mergePreview: (canonicalId, duplicateIds) => api.post('/api/ingredientes/merge-preview', { canonical_id: canonicalId, duplicate_ids: duplicateIds }),
  merge: (canonicalId, duplicateIds) => api.post('/api/ingredientes/merge', { canonical_id: canonicalId, duplicate_ids: duplicateIds }),
};

// ─── Proveedores ──────────────────────────────────────────
export const proveedoresAPI = {
  listar: () => api.get('/api/proveedores'),
  crear: (data) => api.post('/api/proveedores', data),
  actualizar: (id, data) => api.put(`/api/proveedores/${id}`, data),
  eliminar: (id) => api.delete(`/api/proveedores/${id}`),
};

// ─── Marketing (Fase 3 — Simulador de Promoción/Combo, Fase 4 — Opportunities) ────
export const marketingAPI = {
  simularPromocion: (payload) => api.post('/api/marketing/simulate-promotion', payload),
  obtenerOportunidades: () => api.get('/api/marketing/opportunities'),
};

// ─── Categorías ───────────────────────────────────────────
export const categoriasAPI = {
  listar: () => api.get('/api/categorias'),
  crear: (data) => api.post('/api/categorias', data),
  eliminar: (id) => api.delete(`/api/categorias/${id}`),
};

// ─── Campañas (Campaign Tracker, Fase 4.3) ────────────────
// GET /api/marketing/campanas acepta ?estado/?objetivo (estado filtra el
// estado EFECTIVO, no el crudo almacenado — ver backend/routes/marketing.js).
// PUT es reemplazo completo (no PATCH), igual que objetivosAPI.guardar.
// cancelar() usa DELETE porque el backend hace soft-cancel (estado:
// 'cancelada'), nunca un borrado físico — mismo patrón que
// ventas.js DELETE /ordenes/:id.
export const campanasAPI = {
  listar: (params) => api.get('/api/marketing/campanas', { params }),
  obtener: (id) => api.get(`/api/marketing/campanas/${id}`),
  crear: (payload) => api.post('/api/marketing/campanas', payload),
  actualizar: (id, payload) => api.put(`/api/marketing/campanas/${id}`, payload),
  cancelar: (id) => api.delete(`/api/marketing/campanas/${id}`),
  // Fase 5.3 — Campaign Performance. SOLO LECTURA (GET), mismo patrón que
  // obtener(id) — nunca un segundo cliente/interceptor. El backend responde
  // OBSERVED COMPARISON, nunca causal (ver backend/utils/campanasPerformance.js).
  performance: (id) => api.get(`/api/marketing/campanas/${id}/performance`),
  // Fase 5.4.6 — Campaign Learn & Recommend. SOLO LECTURA (GET), mismo
  // patrón que performance(id) — nunca llama a Performance ni a la
  // inteligencia anual de KIQ desde aquí (ver
  // backend/services/kiq/campanasPerformanceRules.js).
  recommendation: (id) => api.get(`/api/marketing/campanas/${id}/recommendation`),
};

// ─── Objetivos (Goals Tracker) ────────────────────────────
// PUT hace upsert (crear/editar es la misma llamada — el backend decide si
// inserta o actualiza según la clave única). DELETE es idempotente: un
// {deleted:false} es una respuesta 200 válida (objetivo ya no existía), no
// un error — nunca se trata como fallo en el frontend.
export const objetivosAPI = {
  obtener: (anio) => api.get(`/api/objetivos/${anio}`),
  guardar: (anio, data) => api.put(`/api/objetivos/${anio}`, data),
  eliminar: (anio, periodType, periodKey, metric) => api.delete(`/api/objetivos/${anio}/${periodType}/${periodKey}/${metric}`),
};

export default api;
